import L from 'leaflet';

// Pure geometry / route-name helpers for the Monitoring route map.

const toRad = (d) => (d * Math.PI) / 180;

// Compass bearing (degrees, 0 = north, clockwise) from point a to b. Used to
// rotate the direction arrows so they point along the travelled segment.
export function segmentBearing(a, b) {
  const toDeg = (r) => (r * 180) / Math.PI;
  const lat1 = toRad(a[0]);
  const lat2 = toRad(b[0]);
  const dLng = toRad(b[1] - a[1]);
  const y = Math.sin(dLng) * Math.cos(lat2);
  const x =
    Math.cos(lat1) * Math.sin(lat2) -
    Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLng);
  return (toDeg(Math.atan2(y, x)) + 360) % 360;
}

// Halfway along a polyline by cumulative length, plus the bearing of the
// segment it falls on — so a direction arrow sits ON a road-following line
// instead of at the straight-chord midpoint, which can be blocks off the
// drawn road. A two-point chord yields its plain midpoint. Planar lengths
// (longitude scaled by cos lat) are fine at city scale. Null below 2 points.
export function pathMidpoint(positions) {
  if (!Array.isArray(positions) || positions.length < 2) return null;
  const lengths = [];
  let total = 0;
  for (let i = 1; i < positions.length; i += 1) {
    const [aLat, aLng] = positions[i - 1];
    const [bLat, bLng] = positions[i];
    const d = Math.hypot(
      bLat - aLat,
      (bLng - aLng) * Math.cos(toRad((aLat + bLat) / 2)),
    );
    lengths.push(d);
    total += d;
  }
  let remaining = total / 2;
  let i = 0;
  while (i < lengths.length - 1 && remaining > lengths[i]) {
    remaining -= lengths[i];
    i += 1;
  }
  const a = positions[i];
  const b = positions[i + 1];
  const t = lengths[i] > 0 ? remaining / lengths[i] : 0;
  return {
    point: [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t],
    bearing: segmentBearing(a, b),
  };
}

// Route names encode the shift as an `_am_` / `_pm_` token (e.g.
// "ny_nyc_tue_pm_carteret"). Returns 'am' | 'pm' | null — null means the shift
// can't be determined, so the route only shows under the "All" filter.
export function getRouteShift(routeName) {
  if (!routeName) return null;
  const match = /(?:^|_)(am|pm)(?:_|$)/i.exec(routeName);
  return match ? match[1].toLowerCase() : null;
}

// Decode a Google encoded polyline (precision 5) into [lat, lng] pairs.
// Format: https://developers.google.com/maps/documentation/utilities/polylinealgorithm
// Hand-rolled (not @mapbox/polyline) per the FrontEnd/.npmrc supply-chain
// guardrail; arithmetic replaces the usual bitwise ops (repo lints no-bitwise).
// Malformed input returns [] so callers fall back to a straight line.
export function decodePolyline(encoded) {
  if (typeof encoded !== 'string' || encoded.length === 0) return [];
  const points = [];
  let index = 0;
  let lat = 0;
  let lng = 0;
  // One zigzag varint; null on truncation or a char outside ASCII 63–126.
  const nextDelta = () => {
    let result = 0;
    let shift = 0;
    let byte;
    do {
      if (index >= encoded.length) return null;
      byte = encoded.charCodeAt(index) - 63;
      index += 1;
      if (byte < 0 || byte > 63) return null;
      result += (byte % 32) * 2 ** shift;
      shift += 5;
    } while (byte >= 32);
    return result % 2 === 1 ? -(result + 1) / 2 : result / 2;
  };
  while (index < encoded.length) {
    const dLat = nextDelta();
    const dLng = nextDelta();
    if (dLat === null || dLng === null) return [];
    lat += dLat;
    lng += dLng;
    points.push([lat / 1e5, lng / 1e5]);
  }
  return points;
}

// Collect every plottable position (driver pings + stops) into camera bounds.
// null → nothing to frame; {single} → one point (setView); {bounds} → fit.
// Camera target for a set of {latitude, longitude} points: null with none,
// `{ single }` for one, `{ bounds }` for several.
export function computeBoundsForPoints(points) {
  const positions = (points || [])
    .filter(
      (point) =>
        Number.isFinite(point.latitude) && Number.isFinite(point.longitude),
    )
    .map((point) => [point.latitude, point.longitude]);
  if (positions.length === 0) return null;
  if (positions.length === 1) return { single: positions[0] };
  return { bounds: L.latLngBounds(positions) };
}

// Camera target for driver records: every driver position plus every stop.
export function computeBoundsForDrivers(driverList) {
  return computeBoundsForPoints(
    driverList.flatMap((driver) => [driver, ...(driver.routeStops || [])]),
  );
}
